// Private conversations between a customer and a supplier company (website).
export const maxMessageLength = 4000;

const errorText = {
  SIGN_IN_REQUIRED: 'Sign in to see your messages.',
  CONVERSATION_NOT_FOUND_OR_FORBIDDEN: 'This conversation is not available to this account.',
  POLICY_ACCEPTANCE_REQUIRED: 'Accept the current Terms of use before sending messages. Your message is still here.',
  INVALID_MESSAGE: `Messages must be between 1 and ${maxMessageLength} characters.`,
  BLOCKED: 'Messages are turned off because one of you has blocked the other.',
  BID_CLOSED: 'This bid is no longer open, so new messages cannot be sent.',
  JOB_CLOSED: 'This job is no longer open, so new messages cannot be sent.',
  RATE_LIMITED: 'You are sending messages too quickly. Please wait a few minutes and try again.',
  INVALID_PHONE: 'Enter a valid phone number, for example +44 7700 900123.',
  NO_EMAIL_ON_ACCOUNT: 'There is no email address on your account to share.',
  CONTACT_REQUIRED: 'Choose at least one way to be contacted.',
  DUPLICATE_REQUEST_ID: 'Something went wrong sending this message. Please try again.',
};

export function sendBlockedText(reason) { return reason ? errorText[reason] || null : null; }

function friendly(error) {
  const code = Object.keys(errorText).find(key => String(error?.message || '').includes(key));
  return new Error(code ? errorText[code] : (error?.message || 'That did not work. Please try again.'));
}

async function call(client, fn, args) {
  if (!client) throw new Error('Messages are not connected. Please try again later.');
  const { data, error } = await client.rpc(fn, args);
  if (error) throw friendly(error);
  return data;
}

export const startBidConversation = (client, kind, bidId) => call(client, 'start_or_get_bid_conversation', { p_kind: kind, p_bid_id: bidId });
export const getWorkConversation = (client, workId) => call(client, 'get_work_conversation', { p_work_id: workId });
export const getConversation = (client, id) => call(client, 'get_conversation', { p_conversation_id: id });
export const markConversationRead = (client, id) => call(client, 'mark_conversation_read', { p_conversation_id: id });
export const revokeContactShare = (client, id) => call(client, 'revoke_contact_share', { p_conversation_id: id });
export async function listMyConversations(client) { return (await call(client, 'list_my_conversations')) || []; }
export async function getUnreadMessageCount(client) { return Number(await call(client, 'get_unread_message_count')) || 0; }

export async function listMessages(client, id, { before = null, after = null, limit = 50 } = {}) {
  return (await call(client, 'list_conversation_messages', { p_conversation_id: id, p_before: before, p_after: after, p_limit: limit })) || [];
}

export function sendMessage(client, id, body, clientRequestId) {
  const text = String(body || '').trim();
  if (!text || text.length > maxMessageLength) return Promise.reject(new Error(errorText.INVALID_MESSAGE));
  return call(client, 'send_message', { p_conversation_id: id, p_body: text, p_client_request_id: clientRequestId });
}

export function shareContact(client, id, { shareEmail, phone }) {
  const cleanPhone = String(phone || '').trim() || null;
  if (!shareEmail && !cleanPhone) return Promise.reject(new Error(errorText.CONTACT_REQUIRED));
  return call(client, 'share_contact', { p_conversation_id: id, p_share_email: Boolean(shareEmail), p_phone: cleanPhone });
}

export function newClientRequestId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (globalThis.crypto?.getRandomValues) globalThis.crypto.getRandomValues(bytes); else for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40; bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map(b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** One copy of each message, oldest first. */
export function mergeMessages(held, incoming) {
  const byId = new Map(held.map(row => [row.id, row]));
  for (const row of incoming) byId.set(row.id, row);
  return [...byId.values()].sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id));
}

/** Calls onChange on realtime inserts when available, and on a polling timer as a fallback. Returns a stop function. */
export function watchConversation(client, id, onChange, pollMs = 15000) {
  const timer = setInterval(onChange, pollMs);
  let channel = null;
  if (client && typeof client.channel === 'function') {
    try {
      channel = client.channel(`conversation:${id}`)
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'conversation_messages', filter: `conversation_id=eq.${id}` }, () => onChange())
        .subscribe(status => { if (status === 'SUBSCRIBED') onChange(); });
    } catch { channel = null; }
  }
  return () => { clearInterval(timer); if (channel) void Promise.resolve(client.removeChannel(channel)).catch(() => undefined); };
}

export function formatMessageTime(iso, now = new Date()) {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const time = at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  if (at.toDateString() === now.toDateString()) return time;
  return `${at.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} ${time}`;
}
